import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appPackApi, appPackTm7, AppPackPreview, AppPackSent, AppPackTm7Source } from '../../utils/api/apppack';
import { fmtDate, fmtDateTime } from '../../utils/region';
import { useStore } from '../../store';

// ── Compile & email application documents (report 54fd6fc9) ──────────────────────────────────────────────────────────
// The reporter: "If the documents are not on file just highlight that you could not find them. The PDF should go to any
// address that is typed in. The files should go into one merged PDF in checklist order, with a cover page listing what's
// included and what's missing." The list is read from Our Documents by the server (services/jobAppPack.ts) and read
// again whenever the visa expiry is changed here; Send compiles it once more with the same expiry, so what goes out is
// what was last shown. The To box opens on the operator's own login address — any address may replace it.

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const split = (v: string): string[] => v.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
const fmtSize = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const btn: React.CSSProperties = { padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };
const ghost: React.CSSProperties = { padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' };
const box: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', fontSize: 13, background: 'var(--bg-app)', color: 'var(--text-primary)' };
// "Not found on file" is highlighted, on screen as on the cover page.
const missingPill: React.CSSProperties = { display: 'inline-block', fontSize: 11.5, fontWeight: 800, color: '#B91C1C', background: '#FEF08A', borderRadius: 6, padding: '1px 7px' };
const amber: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: '#92400E' };
const linkBtn: React.CSSProperties = { padding: 0, border: 'none', background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' };
// The TM.7 row, read as the server reads a document type: case, dots and spaces aside ("TM.7", "TM 7", "TM7").
const isTm7 = (docName: string) => docName.toLowerCase().replace(/[^a-z0-9]+/g, '') === 'tm7';
// The entries whose window opens before the six months' date — the yearly filings (the server's YEARLY_TYPES, report
// c7fd8843) — by the date each opens on, in checklist order, shown under "Files dated from" as on the cover page.
const earlierWindows = (p: AppPackPreview | null): { from: string; names: string[] }[] => {
  const out: { from: string; names: string[] }[] = [];
  if (!p) return out;
  for (const r of p.rows) {
    if (!r.from || r.from >= p.from) continue;
    const g = out.find(x => x.from === r.from);
    if (g) g.names.push(r.docName); else out.push({ from: r.from, names: [r.docName] });
  }
  return out;
};
// The entries of any date, whose `from` is '' — a Company Certificate (the server's ANY_DATE_TYPES, report c7fd8843,
// opening #3) — named on a line of their own under the yearly ones, as on the cover page.
const anyDateEntries = (p: AppPackPreview | null): string[] => (p ? p.rows.filter(r => !r.from).map(r => r.docName) : []);

export default function AppPackDialog({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const myEmail = useStore(s => s.me?.email) || '';
  const [p, setP] = useState<AppPackPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState('');          // the visa expiry typed here; '' = the stamp on file
  const [to, setTo] = useState(myEmail);
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState<AppPackSent | null>(null);
  // The filled-in TM.7 (opening #5): read fresh from the passport at every click, so it can take a while.
  const [tm7, setTm7] = useState<{ busy: boolean; source: AppPackTm7Source | ''; err: string }>({ busy: false, source: '', err: '' });
  const seq = useRef(0);
  const timer = useRef<any>(null);

  const load = (expiry: string) => {
    const n = ++seq.current;
    setLoading(true); setErr('');
    appPackApi.preview(jobId, expiry)
      .then(r => { if (n === seq.current) setP(r); })
      .catch((e: any) => { if (n === seq.current) setErr(e?.message || String(e)); })
      .finally(() => { if (n === seq.current) setLoading(false); });
  };
  useEffect(() => { load(''); return () => clearTimeout(timer.current); }, [jobId]); // eslint-disable-line
  // A date is read again once it is whole; clearing the box goes back to the stamp on file.
  const onExpiry = (v: string) => {
    setTyped(v);
    clearTimeout(timer.current);
    if (!v || /^\d{4}-\d{2}-\d{2}$/.test(v)) timer.current = setTimeout(() => load(v), 400);
  };

  const send = async () => {
    const toList = split(to), ccList = split(cc);
    if (!toList.length) { setErr(t('apk_bad_to')); return; }
    const bad = toList.concat(ccList).find(e => !validEmail(e));
    if (bad) { setErr(t('apk_bad_addr', { addr: bad })); return; }
    setBusy(true); setErr('');
    try { setSent(await appPackApi.send(jobId, { to: toList.join(', '), cc: ccList.join(', '), expiry: typed })); }
    catch (e: any) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const openTm7 = async () => {
    const tab = window.open('', '_blank');   // within the click, so no pop-up blocker stops it
    setTm7({ busy: true, source: '', err: '' });
    try { setTm7({ busy: false, source: await appPackTm7(jobId, typed, tab), err: '' }); }
    catch (e: any) { setTm7({ busy: false, source: '', err: e?.message || String(e) }); }
  };

  // A Shareholder List with none on file is read off the newest Company Documents: their last page alone goes in.
  const sentName = (n: string, lastPage?: string) => (lastPage && n === lastPage ? t('apk_last_page', { name: n }) : n);

  const anyDate = anyDateEntries(p);
  const attachable = !!p && p.rows.some(r => !!r.filled || r.files.some(f => !f.noEmail));
  const canSend = !!p && !loading && !busy && attachable && !p.warning;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={{ width: 700, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800 }}>{t('apk_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3, lineHeight: 1.45 }}>{t('apk_intro')}</div>
        {p && (
          <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 10 }}>
            {[p.client, p.company].filter(Boolean).join(' · ') || '—'}
            <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> — {t('apk_job', { no: p.jobNo })}{p.service ? ` · ${p.service}` : ''}</span>
          </div>
        )}
        {p?.warning && <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 8, padding: '6px 10px', marginTop: 8 }}>{p.warning}</div>}

        {sent ? (
          // ── What went out ──
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#15803D', background: '#ECFDF5', border: '1px solid #86EFAC', borderRadius: 8, padding: '8px 10px' }}>
              <i className="ti ti-circle-check" style={{ marginRight: 6 }} aria-hidden />
              {t('apk_sent', { when: fmtDateTime(sent.sentAt), to: sent.to.join(', '), file: sent.fileName, size: fmtSize(sent.size), included: sent.included, wanted: sent.wanted })}
              {sent.cc.length > 0 && <div style={{ fontWeight: 400, marginTop: 3 }}>{t('apk_sent_cc', { cc: sent.cc.join(', ') })}</div>}
              <div style={{ fontWeight: 400, marginTop: 3 }}>{t('apk_from', { from: sent.from })}</div>
            </div>
            {sent.rows.map((r, i) => (
              <div key={i} style={{ padding: '6px 2px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                <div style={{ fontWeight: 700 }}>{i + 1}. {r.docName}{r.count > 1 ? ` ×${r.count}` : ''}</div>
                {r.included.map(n => <div key={`in:${n}`} style={{ color: 'var(--text-secondary)', paddingLeft: 16 }}><i className="ti ti-check" style={{ color: '#15803D', marginRight: 4 }} aria-hidden />{sentName(n, r.lastPage)}{r.copies && r.count > 1 ? ` — ${t('apk_copies', { n: r.count })}` : ''}</div>)}
                {r.filled && <div style={{ color: 'var(--text-secondary)', paddingLeft: 16 }}><i className="ti ti-check" style={{ color: '#15803D', marginRight: 4 }} aria-hidden />{t('apk_tm7_pdf')} — {t(`apk_tm7_${r.filled}`)}</div>}
                {r.unmerged.map(n => <div key={`un:${n}`} style={{ ...amber, paddingLeft: 16 }}>{sentName(n, r.lastPage)} — {t('apk_sent_unmerged')}</div>)}
                {r.noEmail.map(n => <div key={`ne:${n}`} style={{ ...amber, paddingLeft: 16 }}>{sentName(n, r.lastPage)} — {t('apk_no_email')}</div>)}
                {r.missing > 0 && <div style={{ paddingLeft: 16, marginTop: 2 }}><span style={missingPill}>{r.missing === r.count ? t('apk_not_found') : t('apk_short', { n: r.missing })}</span></div>}
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* ── The visa expiry the six months count back from ── */}
            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 170, flexShrink: 0 }}>
                <label style={lbl}>{t('apk_expiry')}</label>
                <input type="date" value={typed || p?.expiry || ''} disabled={busy} onChange={e => onExpiry(e.target.value)} style={box} />
              </div>
              {p && (
                <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--text-tertiary)', paddingTop: 16, lineHeight: 1.45 }}>
                  {p.expirySource === 'filed' && <div style={{ overflowWrap: 'anywhere' }}>{t('apk_expiry_filed', { name: p.expiryFile })}</div>}
                  {p.expirySource === 'typed' && <div>{p.filedExpiry ? t('apk_expiry_typed', { date: fmtDate(p.filedExpiry) }) : t('apk_expiry_typed_nofile')}</div>}
                  {p.expirySource === 'none' && <div style={{ color: '#92400E', fontWeight: 600 }}>{t('apk_expiry_none')}</div>}
                  <div>{t('apk_window', { date: fmtDate(p.from) })}</div>
                  {earlierWindows(p).map(w => <div key={w.from}>{t('apk_window_yearly', { date: fmtDate(w.from), names: w.names.join(', ') })}</div>)}
                  {anyDate.length > 0 && <div>{t('apk_window_any', { names: anyDate.join(', ') })}</div>}
                </div>
              )}
            </div>

            {/* ── The checklist, as the cover page will show it ── */}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={lbl}>{t('apk_checklist')}</label>
              {p && p.rows.length > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('apk_summary', { found: p.found, wanted: p.wanted })}</span>}
              {loading && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 4 }} aria-hidden />{t('apk_loading')}</span>}
            </div>
            {p && !p.rows.length && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 6 }}>{t('apk_none_listed')}</div>}
            {p && p.rows.map((r, i) => {
              // A TM.7 that is not on file is filled in instead, so it counts as found (r.filled); so does every copy of an
              // entry of copies (a Passport ×2) once its one file is found (r.copies, report 77e4b1e9).
              const short = r.filled || (r.copies && r.files.length) ? 0 : r.count - r.files.length;
              const icon = r.filled ? 'ti-circle-check' : !r.files.length ? 'ti-circle-x' : short > 0 ? 'ti-alert-circle' : 'ti-circle-check';
              const tone = r.filled ? '#15803D' : !r.files.length ? '#B91C1C' : short > 0 ? '#B45309' : '#15803D';
              return (
                <div key={r.key || i} style={{ padding: '6px 2px', borderBottom: '1px solid var(--border)', fontSize: 12.5, opacity: loading ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                    <i className={`ti ${icon}`} style={{ color: tone, fontSize: 15 }} aria-hidden />
                    <span>{i + 1}. {r.docName}{r.count > 1 ? ` ×${r.count}` : ''}</span>
                    {!r.files.length && !r.filled && <span style={missingPill}>{t('apk_not_found')}</span>}
                  </div>
                  {r.filled && <div style={{ paddingLeft: 21, marginTop: 2, fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('apk_tm7_filled')}</div>}
                  {isTm7(r.docName) && (
                    <div style={{ paddingLeft: 21, marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5 }}>
                      <button type="button" onClick={openTm7} disabled={tm7.busy || busy} style={{ ...linkBtn, opacity: tm7.busy ? 0.6 : 1 }}>
                        <i className="ti ti-file-type-pdf" style={{ marginRight: 4 }} aria-hidden />{t('apk_tm7_pdf')}
                      </button>
                      {tm7.busy && <span style={{ color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 4 }} aria-hidden />{t('apk_tm7_busy')}</span>}
                      {!tm7.busy && tm7.source && <span style={tm7.source === 'unread' ? amber : { color: 'var(--text-tertiary)' }}>{t(`apk_tm7_${tm7.source}`)}</span>}
                      {tm7.err && <span style={{ color: '#c0453f' }}>{tm7.err}</span>}
                    </div>
                  )}
                  {r.files.map(f => (
                    <div key={f.nodeId} style={{ paddingLeft: 21, marginTop: 2 }}>
                      <div style={{ overflowWrap: 'anywhere', color: 'var(--text-primary)' }}>{f.lastPage ? t('apk_last_page', { name: f.name }) : f.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtDate(f.date)} · {t('apk_in_folder', { path: f.folder })}</div>
                      {f.noEmail && <div style={amber}>{t('apk_no_email')}</div>}
                    </div>
                  ))}
                  {r.copies && r.count > 1 && r.files.some(f => !f.noEmail) && <div style={{ paddingLeft: 21, marginTop: 2, fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('apk_copies', { n: r.count })}</div>}
                  {short > 0 && r.files.length > 0 && <div style={{ paddingLeft: 21, marginTop: 3 }}><span style={missingPill}>{t('apk_short', { n: short })}</span></div>}
                  {short > 0 && r.older && <div style={{ paddingLeft: 21, marginTop: 2, fontSize: 11, color: 'var(--text-tertiary)', overflowWrap: 'anywhere' }}>{t('apk_older', { name: r.older.name, date: fmtDate(r.older.date), from: fmtDate(r.from || p.from) })}</div>}
                </div>
              );
            })}

            {/* ── Where it goes ── */}
            <div style={{ marginTop: 12 }}>
              <label style={lbl}>{t('apk_to')}</label>
              <input value={to} onChange={e => setTo(e.target.value)} disabled={busy} placeholder="name@example.com" style={box} />
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={lbl}>{t('apk_cc')}</label>
              <input value={cc} onChange={e => setCc(e.target.value)} disabled={busy} style={box} />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
                {t('apk_addr_hint')}{p?.sender ? ` ${t('apk_from', { from: p.sender })}` : ''}
              </div>
            </div>
            {p && !loading && p.rows.length > 0 && !attachable && <div style={{ fontSize: 12, color: '#92400E', marginTop: 8 }}>{t('apk_nothing')}</div>}
            {p?.lastSend && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8, overflowWrap: 'anywhere' }}>
                {t('apk_last', { when: fmtDateTime(p.lastSend.at), who: p.lastSend.byName || '—', what: p.lastSend.reason })}
              </div>
            )}
          </>
        )}

        {err && <div style={{ fontSize: 12, color: '#c0453f', marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button disabled={busy} onClick={onClose} style={ghost}>{t('apk_close')}</button>
          {!sent && (
            <button disabled={!canSend} onClick={send} style={{ ...btn, opacity: canSend ? 1 : 0.55, cursor: canSend ? 'pointer' : 'default' }}>
              {busy ? <><i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 5 }} aria-hidden />{t('apk_sending')}</> : t('apk_send')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
